import { useEffect, useState } from "react";
import { Star } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

export const ReviewDialog = ({ trigger }) => {
  const [services, setServices] = useState([]);
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ name: "", service_id: "", rating: 0, text: "" });

  useEffect(() => {
    api.get("/services").then((r) => setServices(r.data.services)).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.rating) {
      toast.error("Please pick a star rating");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/reviews", form);
      toast.success("Thanks! Your review will appear once approved.");
      setOpen(false);
      setForm({ name: "", service_id: "", rating: 0, text: "" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="rounded-2xl" data-testid="leave-review-dialog">
        <DialogHeader>
          <DialogTitle>Leave a review</DialogTitle>
          <DialogDescription>Had a job done? Tell others how it went. Reviews appear on the site once approved.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label htmlFor="review-name">Your name</Label>
            <Input id="review-name" data-testid="review-name-input" required value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-11 mt-1 rounded-xl" />
          </div>
          <div>
            <Label>Which service?</Label>
            <Select value={form.service_id} onValueChange={(v) => setForm({ ...form, service_id: v })}>
              <SelectTrigger className="mt-1 rounded-xl" data-testid="review-service-select">
                <SelectValue placeholder="Choose a service" />
              </SelectTrigger>
              <SelectContent>
                {services.map((s) => (
                  <SelectItem key={s.id} value={s.id} data-testid={`review-service-${s.id}`}>{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Rating</Label>
            <div className="flex gap-1 mt-1">
              {[1, 2, 3, 4, 5].map((n) => (
                <button key={n} type="button" onClick={() => setForm({ ...form, rating: n })} data-testid={`review-star-${n}`}
                  className="min-h-[44px] min-w-[44px] flex items-center justify-center">
                  <Star className={`h-7 w-7 transition-colors ${form.rating >= n ? "fill-harvest text-harvest" : "text-border"}`} />
                </button>
              ))}
            </div>
          </div>
          <div>
            <Label htmlFor="review-text">Your review</Label>
            <Textarea id="review-text" data-testid="review-text-input" rows={3} required value={form.text}
              onChange={(e) => setForm({ ...form, text: e.target.value })} className="mt-1 rounded-xl" />
          </div>
          <Button type="submit" disabled={submitting} data-testid="review-submit-button"
            className="w-full rounded-full bg-primary hover:bg-[#21421D] text-white transition-colors">
            {submitting ? "Sending..." : "Submit review"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
